import i18n from '../../i18n';
/**
 * Централизованная обработка ошибок
 * 
 * Обрабатывает различные типы ошибок (API, сетевые, валидация)
 * и предоставляет единый интерфейс для обработки
 */

import logger from '../logger/logger';
import { authAPI } from '../../services/api';

/**
 * Классы ошибок для типизации
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export class NetworkError extends Error {
  constructor(message, originalError = null) {
    super(message);
    this.name = 'NetworkError';
    this.originalError = originalError;
  }
}

export class AuthError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Обработчик ошибок
 */
class ErrorHandler {
  constructor() {
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 секунда
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  /**
   * Проверка, можно ли повторить запрос
   */
  isRetriableError(error) {
    // Сетевые ошибки
    if (!error.response) {
      return true;
    }

    const status = error.response?.status;

    // 5xx ошибки сервера - можно повторить
    if (status >= 500 && status < 600) {
      return true;
    }

    // 429 Too Many Requests - можно повторить после задержки
    if (status === 429) {
      return true;
    }

    // 408 Request Timeout - можно повторить
    if (status === 408) {
      return true;
    }

    return false;
  }

  /**
   * Задержка перед повтором запроса
   */
  async delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Попытка обновить токен
   */
  async refreshAccessToken() {
    // Если уже идет обновление, вернем существующий промис
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    this.refreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem('refreshToken');

        if (refreshToken) {
          // Primary path: use refresh token
          logger.info('🔄 Attempting to refresh access token');

          const tokens = await authAPI.refreshToken(refreshToken);

          if (tokens.accessToken) {
            localStorage.setItem('accessToken', tokens.accessToken);

            if (tokens.refreshToken) {
              localStorage.setItem('refreshToken', tokens.refreshToken);
            }

            logger.info('✅ Access token refreshed successfully');
            return tokens.accessToken;
          } else {
            throw new AuthError('Failed to refresh token');
          }
        }

        // Fallback: re-authenticate with Telegram initData
        const initDataRaw = window?.Telegram?.WebApp?.initData;
        if (initDataRaw) {
          logger.info('🔄 No refresh token, re-authenticating with Telegram initData');

          const authResponse = await authAPI.loginWithTelegram({ initData: initDataRaw });

          if (authResponse.accessToken) {
            localStorage.setItem('accessToken', authResponse.accessToken);

            if (authResponse.refreshToken) {
              localStorage.setItem('refreshToken', authResponse.refreshToken);
            }
            if (authResponse.user?.id) {
              localStorage.setItem('userId', authResponse.user.id);
            }

            logger.info('✅ Re-authenticated with Telegram initData successfully');
            return authResponse.accessToken;
          }
        }

        throw new AuthError('No refresh token available');
      } catch (error) {
        logger.error('❌ Failed to refresh access token', error);

        // Очищаем токены и перенаправляем на авторизацию
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userId');

        throw new AuthError('Token refresh failed');
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Обработка 401 ошибки с автоматическим refresh token
   */
  async handle401Error(error, retryConfig = null) {
    try {
      // Пытаемся обновить токен
      const newToken = await this.refreshAccessToken();

      // Если есть конфигурация для повтора, возвращаем ее с новым токеном
      if (retryConfig) {
        retryConfig.headers.Authorization = `Bearer ${newToken}`;
        return retryConfig;
      }

      // Если нет конфигурации для повтора, просто возвращаем успех
      return { shouldRetry: true, newToken };
    } catch (refreshError) {
      logger.error('Failed to refresh token, redirecting to auth', refreshError);

      // Если не удалось обновить токен, нужно перенаправить на авторизацию
      // Но лучше не делать reload, а показать уведомление
      // В Telegram WebApp можно использовать нативный UI

      return { shouldRetry: false, redirectToAuth: true };
    }
  }

  /**
   * Повторить запрос с экспоненциальной задержкой
   */
  async retryRequest(requestFn, attempts = this.retryAttempts, delay = this.retryDelay) {
    let lastError;

    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;

        // Если это не повторяемая ошибка, сразу выбрасываем
        if (!this.isRetriableError(error)) {
          throw error;
        }

        // Если это последняя попытка, выбрасываем ошибку
        if (attempt === attempts) {
          logger.warn(`❌ Request failed after ${attempts} attempts`, {
            attempts,
            error: error.message,
          });
          throw error;
        }

        // Экспоненциальная задержка: 1s, 2s, 4s
        const backoffDelay = delay * Math.pow(2, attempt - 1);

        logger.warn(`⚠️ Request failed, retrying (${attempt}/${attempts}) in ${backoffDelay}ms`, {
          attempt,
          attempts,
          delay: backoffDelay,
          error: error.message,
        });

        await this.delay(backoffDelay);
      }
    }

    throw lastError;
  }

  /**
   * Обработать ошибку API
   */
  handleApiError(error, config) {
    const { url, method } = config || {};
    const status = error.response?.status;
    const data = error.response?.data;

    // Логируем ошибку
    logger.apiError(url, method, status, error, data);

    // Обрабатываем специфичные статусы
    switch (status) {
      case 401:
        // Не обрабатываем здесь - обработаем в interceptor
        return {
          type: 'auth',
          message: 'Unauthorized. Please login again.',
          shouldRetry: false,
          shouldRefreshToken: true,
        };

      case 403:
        return {
          type: 'forbidden',
          message: 'Access forbidden. You don\'t have permission.',
          shouldRetry: false,
        };

      case 404:
        return {
          type: 'notFound',
          message: 'Resource not found.',
          shouldRetry: false,
        };

      case 429:
        return {
          type: 'rateLimit',
          message: 'Too many requests. Please try again later.',
          shouldRetry: true,
          retryAfter: data?.retryAfter || 60,
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          type: 'serverError',
          message: 'Server error. Please try again later.',
          shouldRetry: true,
        };

      default:
        return {
          type: 'unknown',
          message: data?.message || error.message || 'An error occurred',
          shouldRetry: this.isRetriableError(error),
        };
    }
  }

  /**
   * Обработать сетевую ошибку
   */
  handleNetworkError(error, config) {
    const { url, method } = config || {};

    logger.networkError(url, method, error);

    return {
      type: 'network',
      message: 'Network error. Please check your connection.',
      shouldRetry: true,
    };
  }

  /**
   * Универсальная обработка ошибки
   */
  handleError(error, config = null) {
    if (error.response) {
      // Это ошибка API (есть response)
      return this.handleApiError(error, config || error.config);
    } else if (error.request) {
      // Это сетевая ошибка (есть request, но нет response)
      return this.handleNetworkError(error, config || error.config);
    } else {
      // Это другая ошибка
      logger.error('Unknown error', error, { config });
      return {
        type: 'unknown',
        message: error.message || 'An unexpected error occurred',
        shouldRetry: false,
      };
    }
  }

  /**
   * Получить понятное сообщение об ошибке для пользователя
   */
  getUserFriendlyMessage(error) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    const status = error.response?.status;

    switch (status) {
      case 401:
        return i18n.t('error.session_expired_refresh', 'Session expired. Please refresh the page.');
      case 403:
        return i18n.t('error.access_denied_short', 'Access denied.');
      case 404:
        return i18n.t('error.not_found', 'Resource not found.');
      case 429:
        return i18n.t('error.too_many_requests', 'Too many requests. Please wait a moment.');
      case 500:
      case 502:
      case 503:
      case 504:
        return i18n.t('error.server', 'Server error. Please try again later.');
      default:
        if (!error.response) {
          return i18n.t('error.network_short', 'Network error. Please check your connection.');
        }
        return i18n.t('error.generic', 'An error occurred. Please try again.');
    }
  }
}

// Создаем единственный экземпляр обработчика ошибок
export const errorHandler = new ErrorHandler();

// Экспортируем по умолчанию
export default errorHandler;

